import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge, Button, Card, cx } from "./ui";
import { api, ApiError, type McpToolSwitches } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
/** Logiful: welche Werkzeuge der Assistent nutzen darf – global und je Kunde */
export function McpAdminCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mcp-admin"], queryFn: api.mcp.adminTools });
  const [v, setV] = useState<McpToolSwitches | null>(null);
  const [pick, setPick] = useState("");
  useEffect(() => { if (q.data && !v) setV(q.data.values); }, [q.data, v]);
  const save = useMutation({
    mutationFn: (x: McpToolSwitches) => api.mcp.saveAdminTools(x),
    onSuccess: (x) => { toast.success("Gespeichert", { description: msg("Gilt sofort für alle verbundenen Assistenten.") }); setV(x); void qc.invalidateQueries({ queryKey: ["mcp-admin"] }); },
    onError: (e) => toast.error("Nicht gespeichert", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  if (!q.data || !v) return null;
  const { switches, tenants } = q.data;
  const on = (name: string, tenant?: string) => {
    if (tenant && v.tenants[tenant]?.[name] !== undefined) return v.tenants[tenant]![name]!;
    if (v.global[name] !== undefined) return v.global[name]!;
    return !switches.find((s) => s.name === name)?.sensitive;
  };
  const setGlobal = (name: string, val: boolean) => setV({ ...v, global: { ...v.global, [name]: val } });
  const setTenant = (tv: string, name: string, val: boolean | null) => {
    const cur = { ...(v.tenants[tv] ?? {}) };
    if (val === null) delete cur[name];
    else cur[name] = val;
    setV({ ...v, tenants: { ...v.tenants, [tv]: cur } });
  };
  const exceptions = Object.keys(v.tenants);
  const name = (id: string) => tenants.find((tv) => tv.id === id)?.name ?? id;
  const dirty = JSON.stringify(v) !== JSON.stringify(q.data.values);
  const Toggle = ({ value, onChange, inherited }: { value: boolean; onChange: (x: boolean) => void; inherited?: boolean }) => (
    <button type="button" role="switch" aria-checked={value} onClick={() => onChange(!value)} className={cx("w-8 h-[18px] rounded-full p-0.5 transition-colors shrink-0", value ? "bg-ink" : "bg-border-strong", inherited && "opacity-50")}>
      <span className={cx("block size-3.5 rounded-full bg-surface transition-transform", value && "translate-x-3.5")} />
    </button>
  );
  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-3 border-b border-border font-semibold text-[14px] flex items-center gap-2"><Bot className="size-4 text-fg-muted" /> {t("KI-Assistent (MCP): Werkzeuge")}</div>
      <div className="p-4">
        <p className="text-[12.5px] text-fg-muted">{t("Gesperrte Werkzeuge sieht der Assistent gar nicht. Zusätzlich gelten gebuchte Module und die Rechte der verbundenen Person. „Sensibel“ = standardmäßig aus.")}</p>
        <table className="mt-3 w-full text-[13px]">
          <thead className="text-[12px] text-fg-subtle">
            <tr>
              <th className="text-left font-medium py-1">{t("Werkzeug")}</th>
              <th className="text-center font-medium w-20">{t("Alle Kunden")}</th>
              {exceptions.map((tv) => <th key={tv} className="text-center font-medium w-28 truncate" title={name(tv)}>{name(tv)}</th>)}
            </tr>
          </thead>
          <tbody>
            {switches.map((s) => (
              <tr key={s.name} className="border-t border-border">
                <td className="py-1.5">{t(s.label)} {s.sensitive && <Badge tone="warning">{t("sensibel")}</Badge>}</td>
                <td className="text-center"><span className="inline-flex"><Toggle value={on(s.name)} onChange={(x) => setGlobal(s.name, x)} /></span></td>
                {exceptions.map((tv) => {
                  const own = v.tenants[tv]?.[s.name];
                  return (
                    <td key={tv} className="text-center">
                      <span className="inline-flex items-center gap-1">
                        <Toggle value={on(s.name, tv)} inherited={own === undefined} onChange={(x) => setTenant(tv, s.name, x)} />
                        {own !== undefined && <button className="text-[11px] text-fg-subtle hover:text-fg" onClick={() => setTenant(tv, s.name, null)} title={t("wie „Alle Kunden“")}>↺</button>}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
            <option value="">{t("Ausnahme für Kunden …")}</option>
            {tenants.filter((tv) => !exceptions.includes(tv.id)).map((tv) => <option key={tv.id} value={tv.id}>{tv.name}</option>)}
          </select>
          <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} disabled={!pick} onClick={() => { setV({ ...v, tenants: { ...v.tenants, [pick]: {} } }); setPick(""); }}>{t("Hinzufügen")}</Button>
          {exceptions.map((tv) => (
            <Button key={tv} size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => { const n = { ...v.tenants }; delete n[tv]; setV({ ...v, tenants: n }); }}>{name(tv)}</Button>
          ))}
          <span className="ml-auto flex gap-2">
            {dirty && <Button size="sm" variant="ghost" onClick={() => setV(q.data.values)}>{t("Verwerfen")}</Button>}
            <Button size="sm" variant="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate(v)}>{t("Speichern")}</Button>
          </span>
        </div>
      </div>
    </Card>
  );
}
